import { releaseData } from '@/config/release.config';
import IntlMessages from '@/util/IntlMessages';
import { useAppStateStore } from '@/zusttandStore/appState.store';
import { Button, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'ReducerState';
import { AnimatedCardStatusList } from '@/components/ui/card-status-list';
import type { Card } from '@/components/ui/card-status-list';
import dayjs from 'dayjs';
import moment from 'moment';
import { useScreenSize } from '@/hooks/useScreenSize';

interface LanguageData {
  icon: string;
  languageId: string;
  locale: string;
  name: string;
}

const ReleaseModal = () => {
  const { isDesktop } = useScreenSize();
  const [lang, setLang] = useState<'en' | 'th'>('en');
  const { email, prop_id } = useSelector((state: AppState) => state?.auth?.authUser || {});

  const { setReadCurrentRelease, openReleaseModal, setOpenReleaseModal } = useAppStateStore();

  useEffect(() => {
    const langStr = localStorage.getItem('locale');
    const lang: LanguageData | null = langStr ? (JSON.parse(langStr) as LanguageData) : null;
    const lng = lang?.locale === 'th' ? 'th' : 'en';
    setLang(lng);
  }, [openReleaseModal]);

  // Close + mark as read in IndexedDB (triggers on 'X' or 'Close' button)
  const onClose = async () => {
    setOpenReleaseModal(false);
    if (email && prop_id) {
      await setReadCurrentRelease(email, prop_id);
    }
  };

  const currentRelease = releaseData[lang]?.[0];

  const cards: Card[] = (currentRelease?.release_data ?? []).map((item, i) => {
    let subFeatureContent: React.ReactNode = null;

    if (item.sub_feature && item.sub_feature.length > 0) {
      subFeatureContent = (
        <div className='flex flex-col mt-1'>
          {item.sub_feature.map((feat, idx) => {
            const match = feat.match(/^(.*?)(\[.+\])$/);
            let text = feat;
            let badge = null;

            if (match) {
              text = match[1].trim();
              badge = match[2].trim();
            } else if (feat.trim().startsWith('[')) {
              text = '';
              badge = feat.trim();
            }

            const isBullet =
              text.trim().startsWith('สามารถ') ||
              text.trim().startsWith('Transfer') ||
              text.trim().startsWith('Move') ||
              text.trim().startsWith('Block');

            if (!text && badge) {
              return (
                <div key={idx} className='mt-1 mb-2'>
                  <span
                    style={{
                      color: '#0958d9',
                      backgroundColor: '#e6f7ff',
                      border: '1px solid #91d5ff',
                    }}
                    className='text-xs font-medium px-2 py-0.5 rounded-sm inline-block shadow-sm'
                  >
                    {badge}
                  </span>
                </div>
              );
            }

            return (
              <div key={idx} className='flex flex-col gap-1 mb-1.5'>
                <div className={`flex items-start ${isBullet ? 'gap-1.5' : 'gap-0'}`}>
                  <span className='text-xs mt-[1.5px] text-gray-400 mr-1'>●</span>
                  <span className='flex-1 leading-relaxed text-black/75 text-sm'>{text}</span>
                </div>
                {badge && (
                  <div className={isBullet ? 'pl-3.5 mt-0.5' : 'mt-0.5'}>
                    <span
                      style={{
                        color: '#0958d9',
                        backgroundColor: '#e6f7ff',
                        border: '1px solid #91d5ff',
                      }}
                      className='text-xs font-medium px-2 py-0.5 rounded-sm inline-block shadow-sm'
                    >
                      {badge}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    let imageContent: React.ReactNode = null;
    if (item.image) {
      imageContent = (
        <div
          className='mt-2.5 mb-1 overflow-hidden rounded-md border shadow-[0_2px_8px_rgba(0,0,0,0.04)] bg-white p-1 w-full'
          style={{ borderColor: 'rgba(3,143,222,0.15)' }}
        >
          <img
            src={item.image}
            alt={item.release_feature}
            className='w-full h-auto object-contain rounded shadow-sm bg-gray-50/50'
            style={{ border: '1px solid rgba(0,0,0,0.03)' }}
          />
        </div>
      );
    }

    const hasDescription = subFeatureContent || imageContent;
    let descriptionContent = hasDescription ? (
      <div className='flex flex-col'>
        {subFeatureContent}
        {imageContent}
      </div>
    ) : undefined;

    return {
      id: String(i + 1),
      title: item.release_feature,
      status: (item.status as Card['status']) ?? 'completed',
      description: descriptionContent,
    };
  });

  const versionLabel = `v${currentRelease?.version ?? process.env.REACT_APP_VERSION}`;
  const releaseDateRaw = currentRelease?.release_date;
  const releaseDateLabel = releaseDateRaw ? moment(releaseDateRaw).format('DD/MM/YYYY') : '';

  return (
    <Modal
      title={
        <div className='flex items-center gap-2'>
          <span className='font-bold' style={{ color: '#038fde', fontSize: 16 }}>
            Fromas Cloud {versionLabel}
          </span>
          <span className='text-sm font-normal' style={{ color: 'rgba(0,0,0,0.35)' }}>
            ({releaseDateLabel})
          </span>
        </div>
      }
      open={openReleaseModal}
      closable
      maskClosable
      onCancel={onClose}
      footer={null}
      width={isDesktop ? '65%' : '90%'}
      bodyStyle={{ padding: '0 0 0 0' }}
    >
      {/* Header */}
      <div
        className='flex items-center gap-2 px-6 pt-4 pb-3'
        style={{ borderBottom: '1px solid rgba(3,143,222,0.1)' }}
      >
        <span
          className='text-lg font-bold'
          style={{
            background: 'linear-gradient(90deg, #038fde 0%, #60a5fa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {lang === 'en' ? "What's New" : 'มีอะไรใหม่'}
        </span>
        <span>🎉</span>
        <span
          className='ml-auto text-sm font-medium px-2 py-0.5 rounded-full'
          style={{ background: 'rgba(3,143,222,0.08)', color: '#038fde' }}
        >
          {cards.length} features
        </span>
      </div>

      {/* Scrollable Card List */}
      <div
        className='csl-scrollbar overflow-y-auto'
        style={{ maxHeight: '65vh', minHeight: '300px', padding: '12px 16px' }}
      >
        <AnimatedCardStatusList title={versionLabel} cards={cards} className='' />
      </div>

      {/* Footer */}
      <div
        className='flex justify-end px-6 py-3'
        style={{ borderTop: '1px solid rgba(3,143,222,0.1)' }}
      >
        <Button type='primary' onClick={onClose}>
          <IntlMessages id='app.global.bt.close' />
        </Button>
      </div>
    </Modal>
  );
};

export default ReleaseModal;
